import React, { useContext, useEffect } from "react";
import { CallOption, CallOptions, ResultContext } from "../ResultContext";
import "./CallOptionsInput.css";
import { DropdownBox } from "../components/DropdownBox";
import { DecimalInput } from "./DecimalInput";
import { GivenQuestions } from "../constants/Questions";
import { LampSuggestion } from "../components/LampSuggestion";

export function CallOptionsInput({
  questionId,
  foundSubPhase,
}: {
  questionId: number;
  foundSubPhase:
    | {
        id: number;
        name: string;
        phase: number;
        position: number;
      }
    | undefined;
}) {
  const { result, setResult } = useContext(ResultContext);

  const currentQuestion = GivenQuestions.find((q) => q.id === questionId) as {
    dependsOn: number;
  };

  const dependsOn = currentQuestion.dependsOn as number;

  const dependsOnResult = (result[dependsOn] as boolean) || false;

  const foundResult = (result[questionId] as {
    vested: CallOptions;
    unvested: CallOptions;
    vestedBad: CallOptions;
  }) || {
    vested: {
      callOptions: true,
      allShares: true,
      percentageOfShares: "",
      shareValue: CallOption.FairMarket,
      shareValueDiscount: "",
      shareValuePrice: "",
    },
    unvested: {
      callOptions: true,
      allShares: true,
      percentageOfShares: "",
      shareValue: CallOption.FairMarket,
      shareValueDiscount: "",
      shareValuePrice: "",
    },
    vestedBad: {
      callOptions: true,
      allShares: true,
      percentageOfShares: "",
      shareValue: CallOption.FairMarket,
      shareValueDiscount: "",
      shareValuePrice: "",
    },
  };

  useEffect(() => {
    if (!result[questionId]) {
      const copy = { ...result };

      copy[questionId] = {
        vested: {
          callOptions: true,
          allShares: true,
          percentageOfShares: "",
          shareValue: CallOption.FairMarket,
          shareValueDiscount: "",
          shareValuePrice: "",
        },
        unvested: {
          callOptions: true,
          allShares: true,
          percentageOfShares: "",
          shareValue: CallOption.FairMarket,
          shareValueDiscount: "",
          shareValuePrice: "",
        },
        vestedBad: {
          callOptions: true,
          allShares: true,
          percentageOfShares: "",
          shareValue: CallOption.FairMarket,
          shareValueDiscount: "",
          shareValuePrice: "",
        },
      };

      setResult(copy);
    }
    // eslint-disable-next-line
  }, [questionId]);

  return (
    <div>
      <CallOptionBox
        callOptions={foundResult.vested}
        setCallOptions={(o) => {
          const copy = { ...result };

          copy[questionId] = {
            ...foundResult,
            vested: o,
          };

          setResult(copy);
        }}
        title={"Vested shares"}
        defaultOpen={true}
      />
      <div className="mt-4" />
      <CallOptionBox
        callOptions={foundResult.unvested}
        setCallOptions={(o) => {
          const copy = { ...result };

          copy[questionId] = {
            ...foundResult,
            unvested: o,
          };

          setResult(copy);
        }}
        title={"Unvested shares"}
        defaultOpen={true}
      />
      <LampSuggestion
        text={
          "Unvested shares are in principle not earned so they should in principle be able to be bought for a lower price."
        }
      />
      <div className="mt-4" />
      {dependsOnResult && foundSubPhase && foundSubPhase.id === 14 ? (
        <CallOptionBox
          callOptions={foundResult.vestedBad}
          setCallOptions={(o) => {
            const copy = { ...result };

            copy[questionId] = {
              ...foundResult,
              vestedBad: o,
            };

            setResult(copy);
          }}
          title={"Vested shares after vesting period"}
          defaultOpen={true}
        />
      ) : null}
    </div>
  );
}

function CallOptionBox({
  callOptions,
  setCallOptions,
  title,
  defaultOpen,
}: {
  callOptions: CallOptions;
  setCallOptions: (o: CallOptions) => void;
  title: string;
  defaultOpen: boolean;
}) {
  return (
    <DropdownBox title={title} defaultOpen={defaultOpen}>
      <>
        <label>Call option</label>
        <select
          value={callOptions.callOptions ? "yes" : "no"}
          onChange={(e) => {
            let result: CallOptions = { callOptions: false };

            if (e.target.value === "yes") {
              result = {
                callOptions: true,
                allShares: true,
                percentageOfShares: "100",
                shareValue: CallOption.FairMarket,
                shareValueDiscount: "0",
                shareValuePrice: "0",
              };
            }

            setCallOptions(result);
          }}
        >
          <option value="no">No</option>
          <option value="yes">Yes</option>
        </select>
        {callOptions.callOptions ? (
          <>
            <div className="flex">
              <div className="flex flex-column">
                <label>
                  {title === "Vested shares" ||
                  title === "Vested shares after vesting period"
                    ? "All vested shares"
                    : "All unvested shares"}
                </label>
                <select
                  value={callOptions.allShares ? "yes" : "no"}
                  onChange={(e) => {
                    if (!callOptions.callOptions) {
                      return;
                    }

                    const value = e.target.value === "yes";

                    setCallOptions({
                      ...callOptions,
                      allShares: value,
                    });
                  }}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
              {!callOptions.allShares ? (
                <div className="flex flex-column ml-4">
                  <label>Percentage of shares</label>
                  <DecimalInput
                    percentage={true}
                    max={100}
                    min={0}
                    value={callOptions.percentageOfShares}
                    onChange={(e) => {
                      if (!callOptions.callOptions) {
                        return;
                      }

                      setCallOptions({
                        ...callOptions,
                        percentageOfShares: e,
                      });
                    }}
                  />
                </div>
              ) : null}
            </div>
            <div className="flex">
              <div className="flex flex-column">
                <label>Share value</label>
                <select
                  className="wide"
                  value={callOptions.shareValue}
                  onChange={(e) => {
                    if (!callOptions.callOptions) {
                      return;
                    }

                    setCallOptions({
                      ...callOptions,
                      shareValue: e.target.value as CallOption,
                    });
                  }}
                >
                  {Object.values(CallOption).map((item, index) => {
                    return (
                      <option value={item} key={index}>
                        {item}
                      </option>
                    );
                  })}
                </select>
              </div>
              {callOptions.shareValue === CallOption.FairMarketDiscount ? (
                <div className="flex flex-column ml-4">
                  <label>Discount percentage</label>
                  <DecimalInput
                    max={100}
                    min={0}
                    percentage={true}
                    value={callOptions.shareValueDiscount}
                    onChange={(e) => {
                      if (!callOptions.callOptions) {
                        return;
                      }

                      setCallOptions({
                        ...callOptions,
                        shareValueDiscount: e,
                      });
                    }}
                  />
                </div>
              ) : null}
              {callOptions.shareValue === CallOption.CustomPrice ? (
                <div className="flex flex-column ml-4">
                  <label>Custom price</label>
                  <DecimalInput
                    min={0}
                    value={callOptions.shareValuePrice}
                    percentage={false}
                    onChange={(e) => {
                      if (!callOptions.callOptions) {
                        return;
                      }

                      setCallOptions({
                        ...callOptions,
                        shareValuePrice: e,
                      });
                    }}
                  />
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </>
    </DropdownBox>
  );
}
